import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changeImportantDate,
  describePAApiError,
  getSpecialistFeed,
  startImportantDates,
  type ImportantDateKind,
  type ImportantDateRecurrence,
  type PersonalImportantDate,
  type UpcomingImportantDate,
} from '../../../lib/api/team.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './datas.css';

const KICKER = 'Datas';
const KIND: Record<ImportantDateKind, { label: string; glyph: string }> = {
  birthday: { label: 'Aniversário', glyph: '✦' },
  anniversary: { label: 'Celebração', glyph: '∞' },
  personal: { label: 'Momento especial', glyph: '◇' },
};
const isoToday = () => new Intl.DateTimeFormat('en-CA').format(new Date());
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000);
function annualDate(year: number, monthDay: string) {
  return monthDay === '02-29' && new Date(year, 1, 29).getMonth() !== 1
    ? `${year}-02-28`
    : `${year}-${monthDay}`;
}
function nextDate(date: string, recurrence: ImportantDateRecurrence) {
  if (recurrence === 'once') return date;
  const now = isoToday();
  const year = Number(now.slice(0, 4));
  let next = annualDate(year, date.slice(5));
  if (next < now) next = annualDate(year + 1, date.slice(5));
  return next;
}
const pretty = (date: string) =>
  new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`))
    .replace('.', '');
const whenLabel = (days: number) =>
  days === 0 ? 'Hoje' : days === 1 ? 'Amanhã' : `em ${days} dias`;

function DateForm({ close }: { close?: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<ImportantDateKind>('birthday');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [personName, setPersonName] = useState('');
  const [recurrence, setRecurrence] = useState<ImportantDateRecurrence>('annual');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      changeImportantDate({
        type: 'add',
        kind,
        title,
        date,
        recurrence,
        personName: personName || undefined,
        note: note || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'datas_importantes'] });
      close?.();
    },
    onError: (err) => setError(describePAApiError(err, 'Não consegui guardar essa data agora.')),
  });
  return (
    <section className="pgs-section dts-form">
      <h2 className="pgs-h2">Qual momento volta?</h2>
      <div className="pgs-chips" role="group" aria-label="Tipo de data">
        {(Object.keys(KIND) as ImportantDateKind[]).map((id) => (
          <button
            className={`pgs-chip dts-chip${kind === id ? ' on' : ''}`}
            aria-pressed={kind === id}
            key={id}
            onClick={() => setKind(id)}
          >
            <i aria-hidden="true">{KIND[id].glyph}</i>
            {KIND[id].label}
          </button>
        ))}
      </div>
      <label className="dts-field">
        <span>Nome da data</span>
        <input
          value={title}
          maxLength={90}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex.: Aniversário da mãe"
        />
      </label>
      <div className="dts-pair">
        <label className="dts-field">
          <span>Data</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="dts-field">
          <span>
            Pessoa <i>opcional</i>
          </span>
          <input
            value={personName}
            maxLength={80}
            onChange={(e) => setPersonName(e.target.value)}
            placeholder="Ex.: Ana"
          />
        </label>
      </div>
      <div className="pgs-chips" role="group" aria-label="Repetição">
        <button
          className={`pgs-chip dts-chip${recurrence === 'annual' ? ' on' : ''}`}
          aria-pressed={recurrence === 'annual'}
          onClick={() => setRecurrence('annual')}
        >
          Todo ano
        </button>
        <button
          className={`pgs-chip dts-chip${recurrence === 'once' ? ' on' : ''}`}
          aria-pressed={recurrence === 'once'}
          onClick={() => setRecurrence('once')}
        >
          Uma vez
        </button>
      </div>
      <label className="dts-field">
        <span>
          Uma lembrança <i>opcional</i>
        </span>
        <input
          value={note}
          maxLength={140}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ex.: ela gosta de flores amarelas"
        />
      </label>
      {error && (
        <p className="dts-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions">
        {close && (
          <button className="pgs-btn" onClick={close}>
            Agora não
          </button>
        )}
        <button
          className="pgs-btn solid"
          disabled={!title.trim() || !date || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar data'}
        </button>
      </div>
    </section>
  );
}

function ContactsNote() {
  return (
    <div className="pgs-note">
      <i>✦</i>
      <span>
        <b>Seus contatos também lembram</b>
        <small>Aniversários preenchidos nos seus contatos entram sozinhos nesta lista.</small>
      </span>
    </div>
  );
}

function Dashboard({ dates, onBack }: { dates: UpcomingImportantDate[]; onBack: () => void }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: changeImportantDate,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'datas_importantes'] }),
    onError: (err) => setError(describePAApiError(err, 'Não consegui mudar essa data agora.')),
  });
  const next = dates[0];
  const soon = dates.filter((item) => item.daysUntil <= 30);
  const shown = dates.slice(0, 10);
  return (
    <PageShell
      kicker={KICKER}
      title={
        soon.length
          ? `${soon.length} ${soon.length === 1 ? 'momento' : 'momentos'} por perto`
          : 'Um mês tranquilo'
      }
      lead="Olhei os próximos 30 dias. Deixo à vista as suas datas e as dos seus contatos."
      onBack={onBack}
    >
      {next && (
        <div className="pgs-figure">
          <b>
            {next.daysUntil === 0
              ? 'Hoje'
              : next.daysUntil === 1
                ? 'Amanhã'
                : `${next.daysUntil} dias`}
          </b>
          <small>
            {next.daysUntil === 0
              ? 'é o dia de '
              : next.daysUntil === 1
                ? 'chega '
                : 'faltam para '}
            {next.title}, {pretty(next.nextDate)}
          </small>
        </div>
      )}
      <section className="pgs-section">
        <h2 className="pgs-h2">
          Próximas datas <small>{shown.length}</small>
        </h2>
        {shown.map((item) => (
          <div
            className={`pgs-row${item.daysUntil === 0 ? ' dts-today' : ''}`}
            key={`${item.source}-${item.id}`}
          >
            <span className="pgs-ic dts-glyph" aria-hidden="true">
              {KIND[item.kind].glyph}
            </span>
            <span className="pgs-rowt">
              <b>{item.title}</b>
              <small>
                {pretty(item.nextDate)} · {KIND[item.kind].label}
                {item.source === 'contact' && <em className="dts-tag">do contato</em>}
              </small>
            </span>
            <span className="pgs-val dts-when">{whenLabel(item.daysUntil)}</span>
            {item.source === 'personal' && (
              <button
                className="dts-remove"
                aria-label={`Remover ${item.title}`}
                onClick={() => mutation.mutate({ type: 'delete', id: item.id })}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </section>
      {error && (
        <p className="dts-error" role="alert">
          {error}
        </p>
      )}
      {adding ? (
        <DateForm close={() => setAdding(false)} />
      ) : (
        <div className="pgs-actions">
          <button className="pgs-btn solid" onClick={() => setAdding(true)}>
            Guardar outra data
          </button>
        </div>
      )}
      <ContactsNote />
      <div className="pgs-note">
        <i>“</i>
        <span>
          <b>Pode me perguntar “quem faz aniversário este mês?”</b>
          <small>Respondo só com as datas que você ou seus contatos informaram.</small>
        </span>
      </div>
    </PageShell>
  );
}

export function DatasView() {
  const qc = useQueryClient();
  const back = useSkillBack();
  const query = useQuery({
    queryKey: ['pa-specialist-feed', 'datas_importantes'],
    queryFn: () => getSpecialistFeed('datas_importantes'),
    refetchInterval: (q) => (q.state.data?.generating ? 1500 : false),
  });
  const start = useMutation({
    mutationFn: startImportantDates,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'datas_importantes'] }),
  });
  const config = query.data?.config as
    { configured?: boolean | undefined; dates?: PersonalImportantDate[] } | undefined;
  const payload = query.data?.items.find((item) => item.kind === 'dates_overview')?.details as
    { dates?: UpcomingImportantDate[] } | undefined;
  const dates = useMemo(() => {
    const contacts = (payload?.dates ?? []).filter((item) => item.source === 'contact');
    const local = (config?.dates ?? [])
      .map((item) => {
        const n = nextDate(item.date, item.recurrence);
        return {
          ...item,
          source: 'personal' as const,
          nextDate: n,
          daysUntil: daysBetween(isoToday(), n),
        };
      })
      .filter((item) => item.daysUntil >= 0);
    return [...contacts, ...local].sort((a, b) => a.nextDate.localeCompare(b.nextDate));
  }, [config?.dates, payload?.dates]);

  if (query.isError)
    return (
      <PageShell kicker={KICKER} title="Não consegui abrir suas datas agora" onBack={back}>
        <div className="pgs-actions">
          <button className="pgs-btn solid" onClick={() => query.refetch()}>
            Tentar novamente
          </button>
        </div>
      </PageShell>
    );

  if (config && !config.configured)
    return (
      <PageShell
        kicker={KICKER}
        title="Algumas datas merecem voltar"
        lead="Reúno aniversários, conquistas e momentos especiais num só lugar e aviso antes de chegarem."
        onBack={back}
      >
        <section className="pgs-section">
          {(Object.keys(KIND) as ImportantDateKind[]).map((id) => (
            <div className="pgs-row" key={id}>
              <span className="pgs-ic dts-glyph" aria-hidden="true">
                {KIND[id].glyph}
              </span>
              <span className="pgs-rowt">
                <b>{KIND[id].label}</b>
                <small>
                  {id === 'birthday'
                    ? 'De quem você gosta, todo ano.'
                    : id === 'anniversary'
                      ? 'Casamento, conquistas, recomeços.'
                      : 'Uma lembrança só sua.'}
                </small>
              </span>
            </div>
          ))}
        </section>
        <div className="pgs-actions">
          <button
            className="pgs-btn solid"
            onClick={() => start.mutate()}
            disabled={start.isPending}
          >
            {start.isPending ? 'Abrindo o calendário…' : 'Guardar minhas datas'}
          </button>
        </div>
        <ContactsNote />
      </PageShell>
    );

  if (config?.configured && !dates.length)
    return (
      <PageShell
        kicker={KICKER}
        title="Qual data merece voltar?"
        lead="Pode ser um aniversário, uma conquista ou uma lembrança só sua."
        onBack={back}
      >
        <DateForm />
        <ContactsNote />
      </PageShell>
    );

  if (config?.configured) return <Dashboard dates={dates} onBack={back} />;

  return (
    <PageShell kicker={KICKER} title="Abrindo suas datas" onBack={back}>
      <Skeleton variant="rows" label="Abrindo as suas datas" />
    </PageShell>
  );
}
